import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { ApiError } from "@/api/errors";
import { useForgetLoginHint, useLogin, useLoginHint, useSession } from "@/api/queries/auth";
import { Button, Field, InlineError, Input, PageLoader } from "@/components/ui";
import { applyTenantAccent } from "@/lib/branding";

import { AuthFrame } from "./AuthFrame";

export function safeNext(next: string | null): string {
  // Same-origin paths only: rejects absolute, protocol-relative and "/\\host" style values.
  if (!next || !next.startsWith("/")) return "/";
  try {
    const url = new URL(next, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : "/";
  } catch {
    return "/";
  }
}

export function LoginPage() {
  const session = useSession();
  const hint = useLoginHint();
  const forget = useForgetLoginHint();
  const login = useLogin();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const tenant = hint.data?.tenant ?? null;

  useEffect(() => {
    applyTenantAccent(tenant?.palette);
    return () => applyTenantAccent(null);
  }, [tenant]);

  if (session.isLoading || hint.isLoading) return <PageLoader label="Loading" />;
  if (session.data?.authenticated) return <Navigate to={safeNext(params.get("next"))} replace />;

  const error = login.error instanceof ApiError ? login.error : null;
  return (
    <AuthFrame
      tenant={tenant}
      footer={
        tenant ? (
          <button type="button" className="font-medium text-accent-text hover:underline" onClick={() => forget.mutate()}>
            Use another company
          </button>
        ) : undefined
      }
    >
      <h1 className="text-lg font-semibold text-fg">Sign in</h1>
      <p className="mt-0.5 mb-4 text-sm text-fg-muted">{tenant ? `Welcome back to ${tenant.name}.` : "Use your K-SHOP account."}</p>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate({ email, password }, { onSuccess: () => navigate(safeNext(params.get("next")), { replace: true }) });
        }}
      >
        {error && error.code !== "validation_error" && <InlineError error={error} />}
        <Field label="Email" error={error?.fieldError("email")}>
          <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Password" error={error?.fieldError("password")}>
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" variant="primary" className="mt-1 w-full" loading={login.isPending} disabled={!email || !password}>
          Sign in
        </Button>
      </form>
    </AuthFrame>
  );
}
